import { useQuery } from '@tanstack/react-query'

import type {
  CampoOrdenacaoInscritoSympla,
  ListaInscritosSympla,
  ResultadoSympla,
  TipoInscritoSympla,
} from '../types'

import { api } from '@/lib/api-client'

export function useSympla() {
  return useQuery({
    queryKey: ['sympla'],
    queryFn: async () => {
      const { data } = await api.get<ResultadoSympla>('/api/sympla')
      return data
    },
    placeholderData: (anterior) => anterior,
  })
}

export interface ConsultaInscritosSympla {
  page: number
  perPage: number
  sortBy: CampoOrdenacaoInscritoSympla
  sortDir: 'asc' | 'desc'
  search?: string
  tipo?: TipoInscritoSympla
  categoria?: string
  checkIn?: 'sim' | 'nao'
}

/** Nome sugerido pelo servidor no `Content-Disposition`, com fallback. */
function nomeDoAnexo(disposition: string | undefined, fallback: string): string {
  const encontrado = /filename="([^"]+)"/.exec(disposition ?? '')
  return encontrado?.[1] ?? fallback
}

/** Download por blob, não por `window.open`: a rota exige cookie de sessão. */
async function baixarAnexo(caminho: string, fallback: string): Promise<void> {
  const resposta = await api.get<Blob>(caminho, { responseType: 'blob' })

  const url = URL.createObjectURL(resposta.data)
  const link = document.createElement('a')
  link.href = url
  link.download = nomeDoAnexo(
    resposta.headers['content-disposition'] as string | undefined,
    fallback,
  )
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/** Planilha na estrutura de apuração do time (Resumo/Dados/Executivos/abas). */
export async function baixarExportacaoEvento(): Promise<void> {
  await baixarAnexo('/api/sympla/exportacao', 'evento.xlsx')
}

/** O mesmo conteúdo da planilha, em PDF pronto pra compartilhar. */
export async function baixarExportacaoEventoPdf(): Promise<void> {
  await baixarAnexo('/api/sympla/exportacao/pdf', 'evento.pdf')
}

export function useInscritosSympla(consulta: ConsultaInscritosSympla) {
  return useQuery({
    queryKey: ['sympla', 'inscritos', consulta],
    queryFn: async () => {
      const { data } = await api.get<ListaInscritosSympla>('/api/sympla/inscritos', {
        params: consulta,
      })
      return data
    },
    // Trocar página/filtro mantém a lista anterior visível enquanto carrega.
    placeholderData: (anterior) => anterior,
  })
}
